import { useState, useCallback } from 'react';
import type {
  DeviceFrame,
  DeviceType,
  WhiteboardElement,
} from '../types/whiteboard';
import {
  createDeviceFrame,
  DEVICE_FRAME_PRESETS,
} from '../types/whiteboard';

export function useFrames() {
  const [frames, setFrames] = useState<DeviceFrame[]>([]);

  const addFrame = useCallback((deviceType: DeviceType, x: number, y: number) => {
    const frame = createDeviceFrame(deviceType, x, y, {
      zIndex: frames.length,
    });
    setFrames(prev => [...prev, frame]);
    return frame;
  }, [frames.length]);

  const updateFrame = useCallback((id: string, updates: Partial<DeviceFrame>) => {
    setFrames(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, []);

  const deleteFrame = useCallback((id: string) => {
    setFrames(prev => prev.filter(f => f.id !== id));
    return id; // Return id so caller can unparent children
  }, []);

  const duplicateFrame = useCallback((frame: DeviceFrame, elements: WhiteboardElement[]) => {
    const newFrame = createDeviceFrame(frame.deviceType, frame.x + 40, frame.y + 40, {
      name: `${frame.name} (copy)`,
      viewportWidth: frame.viewportWidth,
      viewportHeight: frame.viewportHeight,
      backgroundColor: frame.backgroundColor,
      zIndex: frames.length,
    });

    // Duplicate child elements
    const childElements = elements.filter(el => el.parentFrameId === frame.id);
    const newChildren: WhiteboardElement[] = childElements.map(el => ({
      ...el,
      id: `el-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      parentFrameId: newFrame.id,
    }));

    newFrame.children = newChildren.map(el => el.id);

    setFrames(prev => [...prev, newFrame]);
    return { frame: newFrame, childElements: newChildren };
  }, [frames.length]);

  const changeDevice = useCallback((frameId: string, newDeviceType: DeviceType) => {
    const preset = newDeviceType !== 'custom' ? DEVICE_FRAME_PRESETS[newDeviceType] : null;
    setFrames(prev => prev.map(f => {
      if (f.id !== frameId) return f;
      return {
        ...f,
        deviceType: newDeviceType,
        viewportWidth: preset?.viewportWidth ?? f.viewportWidth,
        viewportHeight: preset?.viewportHeight ?? f.viewportHeight,
        scrollOffset: 0,
      };
    }));
  }, []);

  /** Add a child element to a frame */
  const addChildToFrame = useCallback((frameId: string, elementId: string) => {
    setFrames(prev => prev.map(f => {
      if (f.id !== frameId) return f;
      if (f.children.includes(elementId)) return f;
      return { ...f, children: [...f.children, elementId] };
    }));
  }, []);

  /** Remove a child element from a frame */
  const removeChildFromFrame = useCallback((frameId: string, elementId: string) => {
    setFrames(prev => prev.map(f => {
      if (f.id !== frameId) return f;
      return { ...f, children: f.children.filter(id => id !== elementId) };
    }));
  }, []);

  /** Find which frame contains a point (in canvas coords) */
  const findFrameAtPoint = useCallback((x: number, y: number): DeviceFrame | null => {
    // Iterate in reverse z-order (topmost first)
    const sorted = [...frames].sort((a, b) => b.zIndex - a.zIndex);
    for (const frame of sorted) {
      const preset = frame.deviceType !== 'custom' ? DEVICE_FRAME_PRESETS[frame.deviceType] : null;
      const chromeTop = 36 + ((preset?.chromeType === 'phone') ? 44 : 0);
      const vpLeft = frame.x;
      const vpTop = frame.y + chromeTop;
      const vpRight = vpLeft + frame.viewportWidth;
      const vpBottom = vpTop + frame.viewportHeight;
      if (x >= vpLeft && x <= vpRight && y >= vpTop && y <= vpBottom) {
        return frame;
      }
    }
    return null;
  }, [frames]);

  /** Auto-arrange children vertically in a frame */
  const autoArrange = useCallback((frameId: string, elements: WhiteboardElement[]): Partial<WhiteboardElement>[] => {
    const frame = frames.find(f => f.id === frameId);
    if (!frame) return [];

    const children = elements
      .filter(el => el.parentFrameId === frameId)
      .sort((a, b) => (a.relativeY ?? a.y) - (b.relativeY ?? b.y));

    const padding = 16;
    const gap = 16;
    let currentY = padding;
    const updates: Partial<WhiteboardElement>[] = [];

    for (const child of children) {
      updates.push({
        id: child.id,
        relativeX: padding,
        relativeY: currentY,
        x: padding,
        y: currentY,
        width: Math.min(child.width, frame.viewportWidth - padding * 2),
      } as any);
      currentY += child.height + gap;
    }
    return updates;
  }, [frames]);

  return {
    frames,
    setFrames,
    addFrame,
    updateFrame,
    deleteFrame,
    duplicateFrame,
    changeDevice,
    addChildToFrame,
    removeChildFromFrame,
    findFrameAtPoint,
    autoArrange,
  };
}
